import moment from 'moment';
import {
  TICKET_TYPE_AGENT,
  TRANSACTION_STATUS_AUTHORIZE,
  TRANSACTION_STATUS_ERROR,
  TRANSACTION_STATUS_DECLINED,
  TRANSACTION_STATUS_CAPTURE,
  TRANSACTION_STATUS_VOIDED
} from '../../constants';

const monthToDateQuery = {
  dateFrom: moment().startOf('month').format('MM/DD/YYYY'),
  dateTo: moment().format('MM/DD/YYYY')
};

export default () => ({
  portalApp: {
    pages: [
      {
        path: '/',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/DashboardPage'))
            .default,
        accessRules: {
          dashboard: ['find', 'get']
        }
      },
      {
        path: '/batch',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/BatchReportPage'))
            .default,
        accessRules: {
          batches: ['find', 'get']
        }
      },
      {
        path: '/batch/:date',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/BatchReportPage'))
            .default,
        accessRules: {
          batches: ['find', 'get']
        }
      },
      {
        path: '/payouts',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/DepositsReportPage'))
            .default,
        request: {
          name: 'depositsReport',
          query: { ...monthToDateQuery, count: 30, page: 1 }
        },
        accessRules: {
          deposits: ['read']
        }
      },
      {
        path: '/payouts-details/:depositId',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/DepositDetailsPage'))
            .default,
        request: {
          name: 'depositsDetails'
        },
        accessRules: {
          deposits: ['read']
        }
      },
      {
        path: '/statements',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/StatementsPage'))
            .default,
        accessRules: {
          statements: ['read']
        }
      },
      {
        path: '/statements/statement',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/StatementViewPage'))
            .default,
        accessRules: {
          statements: ['read']
        }
      },
      {
        path: '/retrievals',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/RetrievalsPage'))
            .default,
        accessRules: {
          retrievals: ['find', 'get']
        }
      },
      {
        path: '/retrievals-details/:merchantId/:id',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/RetrievalsDetailsPage'
            )
          ).default,
        request: {
          name: 'retrievalsDetails'
        },
        accessRules: {
          retrievals: ['find', 'get']
        }
      },
      {
        path: '/disputes',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ChargebacksPage'))
            .default,
        accessRules: {
          disputes: ['find', 'get']
        }
      },
      {
        path: '/dispute/:merchantId/:id',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/ChargebacksDetailsPage'
            )
          ).default,
        request: {
          name: 'chargeBacksDetails'
        },
        accessRules: {
          disputes: ['find', 'get']
        }
      },
      {
        path: '/profile',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ProfilePage'))
            .default,
        request: {
          name: 'profile'
        }
      },
      {
        path: '/users',
        loader: async () =>
          (await import('../../components/pages/PortalApplication/UsersPage')).default,
        request: {
          name: 'settings'
        },
        accessRules: {
          users: ['find', 'get']
        }
      },
      {
        path: '/settings',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SettingsPage'))
            .default
      },
      {
        path: '/oauth-slack-callback',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SlackCallbackPage'))
            .default,
        request: {
          name: 'oauthSlackCallback'
        }
      },
      {
        path: '/oauth-quickbooks-callback',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/QuickbooksCallbackPage'
            )
          ).default,
        request: {
          name: 'oauthQuickbooksCallback'
        }
      },
      {
        path: '/api-keys',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ApiKeysPage'))
            .default,
        accessRules: {
          'api-keys': ['find', 'get']
        }
      },
      {
        path: '/merchant-link-overview',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/MerchantLinkOverviewPage'
            )
          ).default,
        request: {
          name: 'merchantLinkOverview'
        },
        accessRules: {
          'oauth-2-0': ['find', 'get']
        }
      },
      {
        path: '/merchant-link-accounts',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/MerchantLinkAccountsPage'
            )
          ).default,
        request: {
          name: 'merchantLinkAccounts'
        },
        accessRules: {
          'linked-accounts': ['find', 'get']
        }
      },
      {
        path: '/merchant-link-settings',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/MerchantLinkSettingsPage'
            )
          ).default,
        request: {
          name: 'merchantLinkSettings'
        },
        accessRules: {
          'oauth-2-0': ['find', 'get']
        }
      },
      {
        path: '/pci-audit',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/PCIAuditPage'))
            .default,
        request: {
          name: 'pciAudit'
        },
        accessRules: {
          'pci-audit': ['find']
        }
      },
      {
        path: '/irs',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/IRSReportingPage'))
            .default,
        request: {
          name: 'irs',
          query: {
            with_status: 1,
            limit: 1,
            dir: 'desc'
          }
        },
        accessRules: {
          'irs-reporting': ['find', 'get']
        }
      },
      {
        path: '/gift-card-transactions',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/TransactionsPage'))
            .default,
        request: {
          name: 'giftCardTransactions',
          query: {
            aggregateTransactionsByDate: true,
            ...monthToDateQuery
          }
        },
        accessRules: {
          transactions: ['find', 'get']
        }
      },
      {
        path: '/gift-card-transactions/details',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/TransactionsDetailsPage'
            )
          ).default,
        request: {
          name: 'giftCardTransactions',
          options: { notSaveQuery: true }
        },
        accessRules: {
          transactions: ['find', 'get']
        }
      },
      {
        path: '/transaction/details/:transactionId',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/TransactionDetailsPage'
            )
          ).default,
        request: {
          name: 'transactionDetails'
        },
        accessRules: {
          transactions: ['find', 'get']
        }
      },
      {
        path: '/gift-cards-account-balance',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/AccountBalancePage'))
            .default,
        request: {
          name: 'giftCard'
        },
        accessRules: {
          'gift-cards': ['find', 'get']
        }
      },
      {
        path: '/gift-cards/details',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/AccountBalanceDetailsPage'
            )
          ).default,
        request: {
          name: 'giftCardTransactions',
          options: { notSaveQuery: true }
        },
        accessRules: {
          'gift-cards': ['find', 'get']
        }
      },
      {
        path: '/webhooks',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/WebhookEventsPage'))
            .default,
        request: {
          name: 'webhooksEvent'
        },
        accessRules: {
          'webhook-events': ['list']
        }
      },
      {
        path: '/webhooks-endpoints',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/WebhookSettingsPage'))
            .default,
        request: {
          name: 'webhooksEndPoints'
        },
        accessRules: {
          webhooks: ['find', 'get']
        }
      },
      {
        path: '/payment-terminals',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/PaymentTerminalsPage'
            )
          ).default,
        request: {
          name: 'paymentTerminals'
        },
        accessRules: {
          'payment-terminals': ['find', 'get']
        }
      },
      {
        path: '/batch-details/:batch',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/BatchReportDetailsPage'
            )
          ).default,
        request: {
          name: 'batchReportDetails',
          query: {
            status: [
              TRANSACTION_STATUS_ERROR,
              TRANSACTION_STATUS_DECLINED,
              TRANSACTION_STATUS_CAPTURE,
              TRANSACTION_STATUS_VOIDED
            ]
          }
        },
        accessRules: {
          batches: ['find', 'get']
        }
      },
      {
        path: '/auth',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/BatchQueuedReportDetailsPage'
            )
          ).default,
        request: {
          name: 'batchReportAuth',
          query: { status: TRANSACTION_STATUS_AUTHORIZE }
        },
        accessRules: {
          batches: ['find', 'get'],
          transactions: ['find', 'get']
        }
      },
      {
        path: '/transactions',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/TransactionsReportPage'
            )
          ).default,
        request: {
          name: 'transactions',
          query: {
            ...monthToDateQuery,
            'sort-by': 'tranDate',
            'sort-order': 'dsc'
          }
        },
        accessRules: {
          transactions: ['find', 'get', 'update']
        }
      },
      {
        path: '/ach-report',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/AchReportDetailsPage'
            )
          ).default,
        accessRules: {
          transactions: ['find', 'get']
        }
      },
      {
        path: '/vterminal',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/VTerminalPage'))
            .default,
        request: {
          name: 'vterminal'
        },
        accessRules: {
          vterminals: ['find', 'get']
        }
      },
      {
        path: '/customers',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/CustomersPage'))
            .default,
        accessRules: {
          customers: ['find', 'get']
        }
      },
      {
        path: '/customers/:id',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/CustomerDetailsPage'))
            .default,
        request: {
          name: 'customerDetails'
        },
        accessRules: {
          customers: ['find', 'get']
        }
      },
      {
        path: '/subscriptions',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SubscriptionsPage'))
            .default,
        request: {
          name: 'subscriptions'
        },
        accessRules: {
          subscriptions: ['find', 'get']
        }
      },
      {
        path: '/subscriptions/new',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/SubscriptionWizardPage'
            )
          ).default,
        request: {
          name: 'geoCountries'
        },
        accessRules: {
          subscriptions: ['find', 'get']
        }
      },
      {
        path: '/subscriptions/:id',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/SubscriptionDetailsPage'
            )
          ).default,
        request: {
          name: 'subscriptionDetails'
        },
        accessRules: {
          subscriptions: ['find', 'get']
        }
      },
      {
        path: '/invoices',
        loader: async () =>
          (await import('../../components/pages/PortalApplication/InvoicingPage'))
            .default,
        accessRules: {
          invoices: ['find', 'get']
        }
      },
      {
        path: '/invoices/:id',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/InvoiceDetailsPage'))
            .default,
        request: {
          name: 'invoiceDetails'
        },
        accessRules: {
          invoices: ['find', 'get']
        }
      },
      {
        path: '/invoices/draft/:id',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/InvoiceEditPage'))
            .default,
        request: {
          name: 'invoiceDetails'
        },
        accessRules: {
          invoices: ['find', 'get', 'create', 'update']
        }
      },
      {
        path: '/receipts/:id',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ReceiptDetailsPage'))
            .default,
        request: {
          name: 'receiptDetails'
        },
        accessRules: {
          invoices: ['find', 'get']
        }
      },
      {
        path: '/products',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ProductsPage'))
            .default,
        request: {
          name: 'products'
        },
        accessRules: {
          products: ['find', 'get']
        }
      },
      {
        path: '/products/:id',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ProductDetailsPage'))
            .default,
        request: {
          name: 'productDetails'
        },
        accessRules: {
          customers: ['find', 'get', 'create', 'update']
        }
      },
      {
        path: '/capital',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/CapitalPage')).default
      },
      {
        path: '/support',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SupportPage'))
            .default,
        request: {
          name: 'ticketsData',
          query: { ticketType: 'merchant' }
        },
        accessRules: {
          support: ['find', 'get']
        }
      },
      {
        path: '/support/new',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/CreateTicketPage'))
            .default,
        request: {
          name: 'ticketsData',
          query: { ticketType: 'merchant' }
        },
        accessRules: {
          support: ['find', 'get', 'create']
        }
      },
      {
        path: '/support/view/:ticketId',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/ViewTicketPage'))
            .default,
        request: {
          name: 'ticketsDetails'
        },
        accessRules: {
          support: ['find', 'get']
        }
      },
      {
        path: '/applications/new',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/MerchantApplicationPage'
            )
          ).default,
        request: {
          name: 'completeMerchantApp'
        }
      },
      {
        path: '/applications/:id',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/MerchantApplicationPage'
            )
          ).default,
        request: {
          name: 'ticketsData',
          query: {
            ticketType: TICKET_TYPE_AGENT,
            bSignUpTicket: true,
            isActivateYourAccount: true
          }
        }
      },
      {
        path: '/api-logs',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/EventLogsPage'))
            .default,
        request: {
          name: 'ipLog',
          query: monthToDateQuery
        },
        accessRules: {
          'app-logs': ['find', 'get']
        }
      },
      {
        path: '/user-management',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/UserManagementPage'))
            .default,
        accessRules: {
          users: ['read']
        }
      },
      {
        path: '/submerchant-overview',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/FlowPage')).default,
        request: {
          name: 'flowOverview'
        },
        accessRules: {
          'flow-overview': ['find', 'get']
        }
      },
      {
        path: '/submerchant-sub-merchants',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/FlowAccountsPage'))
            .default,
        accessRules: {
          'sub-merchant': ['find', 'get']
        }
      },
      {
        path: '/submerchant-sub-merchants/new',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/CreateFlowAccountsPage'
            )
          ).default,
        request: {
          name: 'subMerchants'
        },
        accessRules: {
          'sub-merchant': ['create']
        }
      },
      {
        path: '/search',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SearchPage')).default,
        request: {
          name: 'search'
        }
      },
      {
        path: '/search/:search',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/SearchPage')).default,
        request: {
          name: 'search'
        }
      },
      {
        path: '/denied',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/DeniedPage')).default
      },
      {
        path: '/payment-history/:token',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/PaymentHistoryPage'))
            .default
      },
      {
        path: '/checkout-forms',
        loader: async () =>
          (await import('./../../components/pages/PortalApplication/CheckoutFormsPage'))
            .default,
        request: {
          name: 'checkoutForms'
        }
      },
      {
        path: '/checkout-forms/new',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/CheckoutFormsPage/Form'
            )
          ).default
      },
      {
        path: '/checkout-forms/:formId',
        loader: async () =>
          (
            await import(
              './../../components/pages/PortalApplication/CheckoutFormsPage/Form'
            )
          ).default,
        request: {
          name: 'checkoutForm'
        }
      }
    ]
  },
  loginApp: {
    pages: [
      {
        path: '/login',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/LoginPage')).default,
        request: {
          name: 'redirectLogin'
        }
      },
      {
        path: '/logout',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/LoginPage')).default,
        request: {
          name: 'logout'
        }
      },
      {
        path: '/forgot-password',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/ForgotPasswordPage'))
            .default
      },
      {
        path: '/reset-password',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/ForgotPasswordPage'))
            .default
      },
      {
        path: '/ghost-login',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/GhostLogin')).default,
        request: {
          name: 'ghostLogin'
        }
      },
      {
        path: '/not-found',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/NotFoundPage')).default
      },
      {
        path: '*',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/NotFoundPage')).default
      }
    ]
  },
  oAuthApp: {
    pages: [
      {
        path: '/oauth/authorize',
        loader: async () =>
          (await import('./../../components/pages/FrontApplication/OAuthPage')).default,
        request: {
          name: 'oAuthApp'
        }
      }
    ]
  }
});
